import { CutlerySolidIcon, InfoOutlineIcon } from '@ikyucom/design-system-icons-react'
import * as Popover from '@radix-ui/react-popover'
import { useAtomValue } from 'jotai'
import { forwardRef } from 'react'
import type { PropsWithChildren, ReactNode, RefObject } from 'react'
import { format } from '~/lib/date/date'
import { isEmpty } from '~/lib/empty'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import { money } from '~/lib/money/money'
import { useIsPC } from '~/lib/ua'
import { css } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'
import { HeadingWithVerticalBorder } from '~/ui/HeadingWithVerticalBorder'
import { createTabs } from '~/ui/Tabs'
import { courses$ } from './courses/query'
import { RestaurantMenuRootFragment } from './query'
import type { Menus } from './query'

export const Menu = forwardRef<
  HTMLDivElement,
  {
    title: string
    tab: Tab
    menu: FragmentType<typeof RestaurantMenuRootFragment>
    headerRef?: RefObject<HTMLDivElement>
  }
>(function Menu(props, ref) {
  const { updatedDate, sections } = getFragmentData(RestaurantMenuRootFragment, props.menu)
  return (
    <div
      className={vstack({
        bgColor: 'white',
        gap: '5',
        alignItems: 'stretch',
        py: { base: '6', _pc: '8' },
        px: { base: '3', _pc: '8' },
      })}
      data-tab={props.tab}
      ref={ref}
    >
      <hgroup className={hstack({ justify: 'space-between', alignItems: 'center' })}>
        <h3
          className={css({ fontSize: { base: 'xl', _pc: '2xl' }, fontWeight: 'bold' })}
          data-tab={props.tab}
          ref={props.headerRef}
        >
          {props.title}
        </h3>
        <p className={hstack({ gap: '0', color: 'gray.700', fontSize: 'xs' })}>
          更新日：{format(updatedDate, 'yyyy/MM/dd')}
          （消費税込）
          <InfoPopover
            content={
              <p>
                ※メニューは最新ではない可能性があります
                <br />
                ※席料・サービス料が別途必要な場合があります
              </p>
            }
          >
            <InfoOutlineIcon className={css({ fontSize: 'md' })} />
          </InfoPopover>
        </p>
      </hgroup>
      <div className={css({ spaceY: '8' })}>
        {sections.map((section, i) => (
          <div key={i} className={vstack({ gap: '4', alignItems: 'stretch' })}>
            {section.title && (
              <hgroup className={vstack({ gap: '1', alignItems: 'stretch' })}>
                <HeadingWithVerticalBorder as="h4">{section.title}</HeadingWithVerticalBorder>
                {section.description && (
                  <p className={css({ fontSize: 'sm' })}>{section.description}</p>
                )}
              </hgroup>
            )}
            <ul className={css({ divideY: 'thin', divideColor: 'gray.200', dsY: '3' })}>
              {section.items.map((item, i) => (
                <li key={i} className={hstack({ gap: '3', alignItems: 'end' })}>
                  <hgroup className={vstack({ gap: '1', alignItems: 'stretch', flex: '1' })}>
                    {item.isRecommended && (
                      <div className={hstack({ gap: '1' })}>
                        <CutlerySolidIcon className={css({ color: 'primary', fontSize: 'md' })} />
                        <p
                          className={css({ color: 'gray.700', fontWeight: 'bold', fontSize: 'sm' })}
                        >
                          おすすめ
                        </p>
                      </div>
                    )}
                    <h5 className={css({ fontSize: 'base', fontWeight: 'bold' })}>{item.name}</h5>
                    {item.description && (
                      <p
                        className={css({
                          whiteSpace: 'pre-wrap',
                          fontSize: 'sm',
                          color: 'gray.700',
                        })}
                      >
                        {item.description}
                      </p>
                    )}
                  </hgroup>
                  {!isEmpty(item.price) && (
                    <p className={css({ fontSize: 'sm' })}>{money(item.price)}円</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
})

function InfoPopover(props: PropsWithChildren<{ content: ReactNode }>) {
  return (
    <Popover.Root>
      <Popover.Trigger>{props.children}</Popover.Trigger>
      <Popover.Content
        align="end"
        alignOffset={-10}
        className={css({
          rounded: 'md',
          shadow: 'outline.lg',
          p: '3',
          bgColor: 'white',
          fontSize: 'sm',
        })}
      >
        {props.content}
        <Popover.Arrow width={12} height={6} className={css({ fill: 'white' })} />
      </Popover.Content>
    </Popover.Root>
  )
}

export const TABS = [
  { value: 'A_LA_CARTE', label: '料理' },
  { value: 'COURSE', label: 'コース' },
  { value: 'DRINK', label: 'ドリンク' },
] as const
export type Tab = (typeof TABS)[number]['value']

const Tabs = createTabs<Tab>()

export function MenuTabs(props: {
  value: Tab
  menus: NonNullable<Menus>
  onClick?: (value: Tab) => void
}) {
  const isPc = useIsPC()
  const courses = useAtomValue(courses$) ?? []

  const tabs = TABS.filter((tab) => {
    switch (tab.value) {
      case 'A_LA_CARTE':
        return props.menus.aLaCarte !== null
      case 'COURSE':
        // コースメニュー登録 か Web 予約可能なコースが 1 件以上ある場合
        return courses.length > 0 || props.menus.course !== null
      case 'DRINK':
        return props.menus.drink !== null
    }
  })

  return (
    <div className={css({ px: { base: '3', _pc: '8' }, _sd: { pt: '3' }, borderBottom: 'medium' })}>
      <Tabs.Root value={props.value} theme="border" semi size={isPc ? 'default' : 'sm'}>
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.value} value={tab.value} onClick={props.onClick}>
            {tab.label}
          </Tabs.Tab>
        ))}
      </Tabs.Root>
    </div>
  )
}
